<template>
  <dashboard-layout title="Feedback Detail" back-to="/feedbacks" back-label="Feedbacks">
    <template v-if="feedback">
      <data-panel :title="feedback.about || 'Feedback'">
        <div class="meta-info">
          <span class="email">{{ feedback.email }}</span>
          <span class="date">{{ formatTimestamp(feedback.timestamp) }}</span>
        </div>
        <div class="feedback-text">{{ feedback.feedback }}</div>
      </data-panel>

      <data-panel v-if="feedback.subscriptionCancelFeedback" title="Cancel Feedback Details">
        <template v-if="feedback.isConvertedBack" #actions>
          <status-badge variant="success" label="Converted Back" />
        </template>
        <div class="cancel-details-grid">
          <div class="cancel-detail-item" v-if="feedback.mainReason">
            <span class="cancel-detail-label">Main Reason</span>
            <span class="cancel-detail-value">{{ feedback.mainReason }}</span>
          </div>
          <div class="cancel-detail-item" v-if="feedback.detailedFeedback">
            <span class="cancel-detail-label">Detailed Feedback</span>
            <span class="cancel-detail-value">{{ feedback.detailedFeedback }}</span>
          </div>
          <div class="cancel-detail-item" v-if="feedback.additionalNote">
            <span class="cancel-detail-label">Additional Note</span>
            <span class="cancel-detail-value">{{ feedback.additionalNote }}</span>
          </div>
          <div class="cancel-detail-item" v-if="feedback.email">
            <span class="cancel-detail-label">Email</span>
            <span class="cancel-detail-value cancel-email">{{ feedback.email }}</span>
          </div>
        </div>
      </data-panel>

      <data-panel v-if="feedback.imageUrls && feedback.imageUrls.length > 0" title="Attached Images">
        <div class="image-gallery">
          <div v-for="(imageUrl, index) in feedback.imageUrls" :key="index" class="image-container">
            <img :src="imageUrl" :alt="`Feedback image ${index + 1}`" @click="openImage(imageUrl)" />
          </div>
        </div>
      </data-panel>

      <data-panel title="Reply Status">
        <template #actions>
          <status-badge
            :variant="feedback.repliedBy ? 'success' : 'warning'"
            :label="feedback.repliedBy ? 'Replied' : 'Pending'"
          />
          <button
            @click="saveReply"
            class="save-reply-btn"
            :disabled="savingReply"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
              <polyline points="17 21 17 13 7 13 7 21"></polyline>
              <polyline points="7 3 7 8 15 8"></polyline>
            </svg>
            {{ savingReply ? 'Saving...' : 'Save Reply' }}
          </button>
        </template>
        <div class="reply-info">
          <div class="reply-field">
            <label>Replied By</label>
            <select
              v-model="replyForm.repliedBy"
              class="replied-select"
              :class="{ 'has-value': replyForm.repliedBy }"
            >
              <option value="">-</option>
              <option value="Tuğçe">Tuğçe</option>
              <option value="Ali">Ali</option>
              <option value="Hakan">Hakan</option>
            </select>
          </div>
          <div class="reply-field">
            <label>Replied At</label>
            <span class="reply-value">{{ formatTimestamp(feedback.repliedAt) }}</span>
          </div>
          <div class="reply-field reply-note-field">
            <label>Reply Note</label>
            <textarea
              v-model="replyForm.replyNote"
              class="reply-note-textarea"
              placeholder="Add a note about the reply..."
              rows="3"
            ></textarea>
          </div>
        </div>
      </data-panel>

      <data-panel v-if="userData" title="User Information">
        <div class="user-info">
          <div class="user-avatar">{{ getUserInitials(userData.properties?.name) }}</div>
          <div class="user-details">
            <div class="user-name">{{ userData.properties?.name || 'Unknown User' }}</div>
            <router-link :to="`/user/${feedback.userId}`" class="view-profile-link">
              View Full Profile
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <circle cx="12" cy="10" r="3"></circle>
                <path d="M7 20.662V19c0-1 .9-2 2-2h6c1.1 0 2 1 2 2v1.662"></path>
              </svg>
            </router-link>
          </div>
        </div>
      </data-panel>
    </template>

    <loading-state v-else-if="loading" label="Loading feedback details..." />

    <empty-state v-else title="Feedback not found" icon="messagecircle">
      <span class="empty-msg">Feedback not found or an error occurred.</span>
      <router-link to="/feedbacks" class="back-link">Return to Feedbacks</router-link>
    </empty-state>

    <div v-if="selectedImage" class="image-modal" @click="selectedImage = null">
      <div class="modal-content">
        <img :src="selectedImage" alt="Enlarged feedback image" />
        <button class="close-button" @click.stop="selectedImage = null">×</button>
      </div>
    </div>
  </dashboard-layout>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { FirebaseFeedbackService } from '../services/firebaseFeedbackService'
import { FirebaseUserService } from '../services/firebaseUserService'
import DashboardLayout from './ui/DashboardLayout.vue'
import DataPanel from './ui/DataPanel.vue'
import StatusBadge from './ui/StatusBadge.vue'
import LoadingState from './ui/LoadingState.vue'
import EmptyState from './ui/EmptyState.vue'

export default {
  name: 'FeedbackDetailView',
  components: {
    DashboardLayout,
    DataPanel,
    StatusBadge,
    LoadingState,
    EmptyState
  },
  setup() {
    const route = useRoute()
    const feedbackService = new FirebaseFeedbackService()
    const userService = new FirebaseUserService()

    const feedback = ref(null)
    const userData = ref(null)
    const loading = ref(true)
    const selectedImage = ref(null)
    const savingReply = ref(false)
    const replyForm = reactive({
      repliedBy: '',
      replyNote: ''
    })

    const loadFeedback = async () => {
      try {
        const feedbackId = route.params.id
        const feedbackData = await feedbackService.getFeedbackById(feedbackId)
        feedback.value = feedbackData

        // Initialize reply form with existing data
        if (feedbackData) {
          replyForm.repliedBy = feedbackData.repliedBy || ''
          replyForm.replyNote = feedbackData.replyNote || ''
        }

        if (feedbackData && feedbackData.userId) {
          const user = await userService.getUserById(feedbackData.userId)
          userData.value = user
        }
      } catch (error) {
        console.error('Error loading feedback:', error)
      } finally {
        loading.value = false
      }
    }

    const formatTimestamp = (timestamp) => {
      if (!timestamp) return '-'

      // Handle Firestore timestamp objects
      if (timestamp.seconds) {
        const date = new Date(timestamp.seconds * 1000)
        return date.toLocaleString('tr-TR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      }

      // Handle regular Date objects or strings
      const date = new Date(timestamp)
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const getUserInitials = (name) => {
      if (!name) return '?'
      return name.split(' ').map(n => n[0]).join('').toUpperCase()
    }

    const openImage = (url) => {
      selectedImage.value = url
    }

    const saveReply = async () => {
      try {
        savingReply.value = true
        const result = await feedbackService.saveReply(
          feedback.value.id,
          replyForm.repliedBy || null,
          replyForm.replyNote || null
        )
        feedback.value.repliedBy = replyForm.repliedBy || null
        feedback.value.replyNote = replyForm.replyNote || null
        feedback.value.repliedAt = result.repliedAt
        alert('Reply saved successfully!')
      } catch (error) {
        console.error('Error saving reply:', error)
        alert('Failed to save reply')
      } finally {
        savingReply.value = false
      }
    }

    onMounted(() => {
      loadFeedback()
    })

    return {
      feedback,
      userData,
      loading,
      selectedImage,
      savingReply,
      replyForm,
      formatTimestamp,
      getUserInitials,
      openImage,
      saveReply
    }
  }
}
</script>

<style scoped>
.meta-info {
  display: flex;
  gap: 16px;
  color: var(--text-secondary);
  font-size: 14px;
  margin-bottom: 16px;
}

.email {
  color: var(--primary-color);
}

.feedback-text {
  background: rgba(255, 255, 255, 0.05);
  padding: 16px;
  border-radius: 12px;
  line-height: 1.6;
  color: var(--text-primary);
  white-space: pre-wrap;
}

.cancel-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  background: rgba(251, 146, 60, 0.05);
  padding: 20px;
  border-radius: 12px;
  border: 1px solid rgba(251, 146, 60, 0.15);
}
.cancel-detail-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.cancel-detail-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary);
}
.cancel-detail-value {
  font-size: 14px;
  color: var(--text-primary);
  line-height: 1.5;
}
.cancel-email {
  color: var(--primary-color);
}

.save-reply-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: linear-gradient(135deg, var(--primary-color), var(--gradient-2));
  border: none;
  border-radius: 10px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(108, 99, 255, 0.3);
}

.save-reply-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(108, 99, 255, 0.4);
}

.save-reply-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.reply-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  background: rgba(255, 255, 255, 0.05);
  padding: 20px;
  border-radius: 12px;
}

.reply-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.reply-field label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-secondary);
}

.reply-value {
  color: var(--text-primary);
  font-size: 14px;
}

.reply-note-field {
  grid-column: 1 / -1;
}

.replied-select {
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.replied-select:hover {
  border-color: var(--primary-color);
}

.replied-select:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(108, 99, 255, 0.2);
}

.replied-select.has-value {
  background: rgba(34, 197, 94, 0.15);
  border-color: rgba(34, 197, 94, 0.4);
  color: #22c55e;
}

.replied-select option {
  background: #1a1b2e;
  color: var(--text-primary);
}

.reply-note-textarea {
  width: 100%;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: all 0.3s ease;
}

.reply-note-textarea:hover {
  border-color: var(--primary-color);
}

.reply-note-textarea:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(108, 99, 255, 0.2);
}

.reply-note-textarea::placeholder {
  color: var(--text-secondary);
  opacity: 0.5;
}

.image-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

.image-container {
  aspect-ratio: 1;
  overflow: hidden;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.image-container:hover {
  transform: scale(1.05);
}

.image-container img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 16px;
  background: rgba(255, 255, 255, 0.05);
  padding: 16px;
  border-radius: 12px;
}

.user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-color), var(--gradient-2));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 600;
  color: white;
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.user-name {
  font-weight: 600;
  color: var(--text-primary);
}

.view-profile-link {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--primary-color);
  font-size: 14px;
  text-decoration: none;
  transition: all 0.3s ease;
  width: fit-content;
}

.view-profile-link:hover {
  opacity: 0.8;
  text-decoration: underline;
}

.empty-msg {
  display: block;
}

.back-link {
  display: inline-block;
  margin-top: 16px;
  padding: 8px 16px;
  background: rgba(108, 99, 255, 0.2);
  border-radius: 8px;
  color: var(--primary-color);
  text-decoration: none;
  transition: all 0.3s ease;
}

.back-link:hover {
  background: rgba(108, 99, 255, 0.3);
}

.image-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
}

.modal-content {
  position: relative;
  max-width: 90%;
  max-height: 90%;
}

.modal-content img {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
}

.close-button {
  position: absolute;
  top: -40px;
  right: -40px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.3);
}

@media (max-width: 1024px) {
  .cancel-details-grid {
    grid-template-columns: 1fr;
  }

  .image-gallery {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }

  .close-button {
    top: -20px;
    right: -20px;
    width: 30px;
    height: 30px;
    font-size: 18px;
  }
}
</style>
